import type { FlagKey } from '@doneverse/shared'
import { useCallback, useMemo } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { resolveEnabledFlags } from '@/lib/feature-flags'
import { EMPTY_FLAGS, useDashboardStore } from '@/store'

const isDevOrE2E = (): boolean => import.meta.env.DEV || import.meta.env.VITE_E2E_TEST_MODE === 'true'

/**
 * Returns the set of enabled feature flags for the current user.
 *
 * Resolution (each layer overrides the previous):
 *   1. Server-resolved flags from `/v1/me` (role rules + overrides + pins).
 *   2. `NATIVE_SHELL_FLAGS` when running in the mobile app with native calls
 *      available (additive, and NOT dev-only).
 *   3. Dev-only store overrides from localStorage / `?flags=…` (additive).
 *   4. Dev-only `feature-flags.local.ts` (typed; can force on OR off).
 */
export function useFeatureFlags() {
  const { profile } = useAuth()

  // In production builds the selector returns the shared EMPTY_FLAGS reference
  // regardless of what's in storage, so the dev-only layer never leaks into
  // prod and the selector is stable across renders.
  const devOverrides = useDashboardStore((s) => (isDevOrE2E() ? s.devFlagOverrides : EMPTY_FLAGS))

  const enabledFlags = useMemo<FlagKey[]>(
    () => resolveEnabledFlags(profile?.feature_flags, devOverrides),
    [profile?.feature_flags, devOverrides],
  )

  const hasFlag = useCallback((flag: FlagKey) => enabledFlags.includes(flag), [enabledFlags])

  const clearFlags = useCallback(() => {
    if (!isDevOrE2E()) return
    useDashboardStore.getState().clearDevFlagOverrides()
  }, [])

  return useMemo(
    () => ({
      flags: enabledFlags,
      devOverrides,
      hasFlag,
      clearFlags,
    }),
    [enabledFlags, devOverrides, hasFlag, clearFlags],
  )
}
